import { CatalogueItem } from '@vfs-digital-channels/ns-utils';
import { JOURNEY_NAME } from 'src/features/common/types/tealium-service';
import { getProductName } from 'src/features/dpo/helpers/tealium-service-helpers';
import {
  ActionType,
  EventName,
  HelpButtonAction,
  JourneyType,
  LinkName,
  PageName,
  ProductList,
} from 'src/features/order/services/order-tealium-services';
import objTealium from 'src/utils/Tealium/Tealium';

enum FormName {
  DELIVERY_ADDRESS = 'delivery address',
  ALLOCATE_DEVICE = 'allocate device to address',
  PERSONAL_DETAILS = 'personal details',
  ADD_DELIVERY_ADDRESS = 'add delivery address',
}

const PAGE_NAME = 'vodapay: buy pos: ';
const TOP_PAGE_NAME = 'vodapay: top: onboarding: ';
const SMME_PAGE_NAME = 'vodapay: smme: buy pos: ';
const BUY_POS = 'buy pos';
const LINK_NAME = 'vodapay: pos: ';
const TOP_LINK_NAME = 'vodapay: top: onboarding: ';

type OrderPageName =
  | 'vodapay: buy pos: '
  | 'vodapay: smme: buy pos: '
  | 'vodapay: top: onboarding: ';
type OrderLinkName = 'vodapay: pos: ';

interface View {
  event_name: EventName | `${EventName}, ${EventName}`;
  page_name?: `${OrderPageName}${PageName}`;
  link_page_name?: string;
  link_name?: `${OrderLinkName}${LinkName}` | `${OrderLinkName}${LinkName}${ActionType}`;
  event_label?: string;
  event_value?: string;
  product_category?: 'POS';
  transaction_id?: string;
  form_name?: FormName;
  form_completion_rate?: string;
  form_field_incomplete?: string;
  event_error_name?: string;
  event_error_code?: 400;
  event_error_type?: 'user error';
  journey_name?: JOURNEY_NAME;
  journey_type?: JourneyType;
  journey_step_name?: string;
  product_name?: string | string[];
}

interface Link {
  event_name?: EventName | `${EventName}, ${EventName}`;
  page_name?: PageName | `${OrderPageName}${PageName}`;
  link_page_name?: string;
  link_name?: `${OrderLinkName}${LinkName}` | HelpButtonAction | LinkName;
  link_id?: string;
  product_name?: string;
  journey_name?: JOURNEY_NAME;
  product_quantity?: number;
  tender_type?: string[];
  form_name?: FormName;
  form_completion_rate?: string;
}

export const staticTealiumVariables = (journey: JOURNEY_NAME) => {
  return {
    page_url: window.location.href,
    page_section: BUY_POS,
    page_channel: `${LINK_NAME}${BUY_POS}`,
    page_subdomain: BUY_POS,
    journey_name: journey,
    page_type: 'website',
  };
};

const staticToPVariables = {
  page_section: 'tap on phone onboarding',
  page_channel: 'tap on phone app',
  page_type: 'webview',
  visitor_customer_type: 'consumer',
};

const attempt = (func: () => void) => {
  try {
    func();
  } catch (err) {
    console.error(err);
    // intentional swallowing of non functional error
  }
};

const sendViewToTealium = (obj: View) => {
  attempt(() => {
    // @ts-ignore
    objTealium.view(obj);
  });
};

const sendLinkToTealium = (obj: Link) => {
  attempt(() => {
    // @ts-ignore
    objTealium.link(obj);
  });
};

const PersonalDetailsAndDeliveryAddressCommonVariables = (
  page: `${OrderPageName}${PageName}`,
  formName: FormName,
  journey: JOURNEY_NAME,
  productName?: string,
): View => {
  return {
    event_name: `${EventName.PAGE_VIEW}, ${EventName.FORM_START}`,
    page_name: page,
    form_name: formName,
    product_name: productName,
    ...staticTealiumVariables(journey),
  };
};

export const getProductListItems = (productList: CatalogueItem[], isPaymentSuccess: boolean) => {
  let products: ProductList = { name: [], quantity: [], price: [], transactionTotal: 0 };

  productList.forEach((product) => {
    const device = product.device;
    const price = product.price.price ? product.price.price / 100 : 0;
    if (product.device.quantity !== undefined) {
      products.name.push(getProductName(device.title.toString()));
      products.quantity.push(device.quantity.toString());
      products.price.push(price.toString());
      products.transactionTotal =
        price !== 0
          ? products.transactionTotal + price * device.quantity
          : products.transactionTotal;
    }
  });

  const productListAnalytics = {
    product_name: products.name,
    product_quantity: products.quantity,
    product_price: products.price,
  };

  if (isPaymentSuccess) {
    return { ...productListAnalytics, transaction_total: products.transactionTotal.toString() };
  }

  return productListAnalytics;
};

export interface IOrderTealiumService {
  onChooseSolutionPageStart(): void;
  onPaymentSelected(paymentMethod: string, linkName: LinkName): void;
  onSelectDeliveryMethod(): void;
  onCartViewed(pageName: PageName): void;
  onUnderageModalView(): void;
  onUnderageModalAction(): void;
  onUnderageModalEnd(): void;
  onTenderTypeStart(): void;
  onPersonalDetailsStart(productName?: string): void;
  onPersonalDetailsEnd(productName?: string): void;
  onPersonalDetailsToPStart(): void;
  onPersonalDetailsToPFormStart(): void;
  onPersonalDetailsToPIdle(incompleteFields: string, formCompletionRate: string): void;
  onPersonalDetailsToPEnd(): void;
  onOrderSummaryStart(productList: CatalogueItem[], transactionId: string | undefined): void;
  onDeliveryAddressStart(): void;
  onOrderCompletePageStart(productList: CatalogueItem[], transactionID?: string): void;
  onConfirmOrderSubmitModalStart(transactionId: string | undefined): void;
  onReduceQuantityModalStart(): void;
  onPaymentFailureStart(): void;
  onHelpButtonActions(pageName: PageName, action: HelpButtonAction): void;
  onCartTypeAction(
    isBasket: boolean,
    pageName: PageName,
    productName: string,
    event: EventName,
  ): void;
  onTenderTypeActions(tenderType: string[]): void;
  onPersonalDetailsActions(journey?: JOURNEY_NAME): void;
  onOrderSummaryAction(linkName: LinkName): void;
  onAddressDeliveryAction(): void;
  onAddressDeliveryDeviceAllocationStart(): void;
  onOrderConfirmAction(linkName: LinkName): void;
  onConfirmOrderSubmitModalAction(): void;
  onReduceQuantityModalAction(): void;
  onPaymentFailureAction(): void;
  onChooseSolutionEnd(productName: string): void;
  onOrderSummaryEnd(): void;
  onAddressDeliveryEnd(): void;
  onAddressDeliveryDeviceAllocationEnd(): void;
  onOrderConfirmEnd(): void;
  onShowTOPModal(): void;
  onCloseTOPModal(): void;
  onGetItHereTOP(): void;
  onShowReserveFundModal(transactionId: string | undefined): void;
  onActionReserveFundModal(actionType: ActionType): void;
  onShowConfirmModal(transactionId: string | undefined): void;
  onActionConfirmFundModal(actionType: ActionType): void;
}

export default class OrderTealiumService implements IOrderTealiumService {
  private journeyName: JOURNEY_NAME;

  constructor(journey: JOURNEY_NAME) {
    this.journeyName = journey;
  }

  onChooseSolutionPageStart(): void {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_START}`,
      page_name: `${PAGE_NAME}${PageName.CHOOSE_SOLUTION}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onPaymentSelected(paymentMethod: string, linkName: LinkName): void {
    sendViewToTealium({
      event_name: EventName.UI_INTERACTION,
      link_page_name: `${SMME_PAGE_NAME}${PageName.ORDER_SUMMARY}`,
      event_label: 'how will the customer be paying',
      link_name: `${LINK_NAME}${linkName}`,
      event_value: paymentMethod,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onSelectDeliveryMethod(): void {
    sendViewToTealium({
      event_name: EventName.UI_INTERACTION,
      link_page_name: `${SMME_PAGE_NAME}${PageName.ORDER_SUMMARY}`,
      link_name: `${LINK_NAME}${LinkName.SELECT_DELIVERY_METHOD}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onCartViewed(pageName: PageName): void {
    sendLinkToTealium({
      event_name: `${EventName.CART_VIEW}, ${EventName.UI_INTERACTION}`,
      page_name: pageName,
      link_page_name: pageName,
      link_name: LinkName.CART_VIEW,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onUnderageModalView(): void {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: `${PAGE_NAME}${PageName.USER_UNDER_18_MODAL}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onUnderageModalAction(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      link_page_name: `${PAGE_NAME}${PageName.USER_UNDER_18_MODAL}`,
      link_id: `${PAGE_NAME}${LinkName.CLOSE_APPLICATION}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onUnderageModalEnd(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      link_page_name: `${PAGE_NAME}${PageName.USER_UNDER_18_MODAL}`,
      link_id: `${PAGE_NAME}${LinkName.CLOSE_MODAL}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onTenderTypeStart(): void {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: `${PAGE_NAME}${PageName.TENDER_TYPE}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  /** ####################
   *   Your details
   * ####################
   */

  onPersonalDetailsStart(productName?: string): void {
    sendViewToTealium(
      PersonalDetailsAndDeliveryAddressCommonVariables(
        `${PAGE_NAME}${PageName.PERSONAL_DETAILS}`,
        FormName.PERSONAL_DETAILS,
        this.journeyName,
        productName,
      ),
    );
  }

  onPersonalDetailsEnd(productName?: string): void {
    sendLinkToTealium({
      event_name: `${EventName.UI_INTERACTION}, ${EventName.FORM_END}`,
      page_name: `${PAGE_NAME}${PageName.PERSONAL_DETAILS}`,
      link_id: `${LINK_NAME}${LinkName.CHECKOUT}`,
      form_name: FormName.PERSONAL_DETAILS,
      product_name: productName,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onPersonalDetailsToPStart(): void {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_STEP}`,
      page_name: `${TOP_PAGE_NAME}${PageName.PERSONAL_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JourneyType.ONBOARDING,
      journey_step_name: 'personal details',
      ...staticToPVariables,
    });
  }

  onPersonalDetailsToPFormStart(): void {
    sendViewToTealium({
      event_name: EventName.FORM_START,
      page_name: `${TOP_PAGE_NAME}${PageName.PERSONAL_DETAILS}`,
      form_name: FormName.PERSONAL_DETAILS,
      ...staticToPVariables,
    });
  }

  onPersonalDetailsToPIdle(incompleteFields: string, formCompletionRate: string): void {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: `${TOP_PAGE_NAME}${PageName.PERSONAL_DETAILS}`,
      form_name: FormName.PERSONAL_DETAILS,
      form_completion_rate: `${formCompletionRate}%`,
      form_field_incomplete: incompleteFields,
      ...staticToPVariables,
    });
  }

  onPersonalDetailsToPEnd(): void {
    sendLinkToTealium({
      event_name: `${EventName.UI_INTERACTION}, ${EventName.FORM_END}`,
      page_name: `${TOP_PAGE_NAME}${PageName.PERSONAL_DETAILS}`,
      link_id: `${TOP_LINK_NAME}${LinkName.PERSONAL_DETAILS}: next`,
      form_name: FormName.PERSONAL_DETAILS,
      form_completion_rate: '100%',
      ...staticToPVariables,
    });
  }

  onOrderSummaryStart(productList: CatalogueItem[], transactionId: string | undefined): void {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.CHECKOUT}`,
      page_name: `${PAGE_NAME}${PageName.ORDER_SUMMARY}`,
      product_category: 'POS',
      transaction_id: transactionId,
      ...staticTealiumVariables(this.journeyName),
      ...getProductListItems(productList, false),
    });
  }

  onDeliveryAddressStart(): void {
    sendViewToTealium(
      PersonalDetailsAndDeliveryAddressCommonVariables(
        `${PAGE_NAME}${PageName.DELIVERY_ADDRESS}`,
        FormName.ADD_DELIVERY_ADDRESS,
        this.journeyName,
      ),
    );
  }

  onOrderCompletePageStart(productList: CatalogueItem[], transactionID?: string): void {
    const eventNameSuffix =
      ', checkout_complete, journey_end, transaction_complete, smme_application_complete, transaction_complete, pro_onboarding_complete';
    sendViewToTealium({
      // @ts-ignore
      event_name: `${EventName.PAGE_VIEW}${eventNameSuffix}`,
      page_name: `${PAGE_NAME}${PageName.ORDER_COMPLETE}`,
      transaction_id: transactionID,
      product_category: 'POS',
      ...getProductListItems(productList, true),
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onConfirmOrderSubmitModalStart(transactionId: string | undefined): void {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: `${PAGE_NAME}${PageName.SUBMIT_APP_CONFIRMATION}`,
      transaction_id: transactionId,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onReduceQuantityModalStart(): void {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: `${PAGE_NAME}${PageName.REDUCE_QUANTITY_CONFIRM}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onPaymentFailureStart(): void {
    const eventErrorName = 'transaction failed: payment gateway';
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.EVENT_ERROR}`,
      page_name: `${PAGE_NAME}${PageName.PAYMENT_FAILED}`,
      event_error_name: eventErrorName,
      event_error_code: 400,
      event_error_type: 'user error',
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onHelpButtonActions(pageName: PageName, action: HelpButtonAction): void {
    sendLinkToTealium({
      event_name: `${EventName.UI_INTERACTION}, ${EventName.SUPPORT_ISSUE_RAISED}`,
      link_page_name: pageName,
      link_name: action,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onCartTypeAction(
    isBasket: boolean,
    pageName: PageName,
    productName: string,
    event: EventName,
  ): void {
    let sendObjToTealium: Link = {};

    if (isBasket) {
      let linkName: LinkName = event === EventName.ADD ? LinkName.CART_ADD : LinkName.CART_REMOVE;
      sendObjToTealium = {
        event_name: `${event}, ${EventName.UI_INTERACTION}`,
        page_name: `${PAGE_NAME}${pageName}`,
        link_name: linkName,
        product_name: productName,
        product_quantity: 1,
      };
    } else {
      let linkName: LinkName =
        event === EventName.CART_ADD ? LinkName.ADD_PRODUCT : LinkName.REMOVE_PRODUCT;
      sendObjToTealium = {
        event_name: `${EventName.UI_INTERACTION}, ${event}`,
        product_name: productName,
        link_page_name: `${PAGE_NAME}${pageName}`,
        link_id: `${LINK_NAME}${linkName}`,
      };
    }

    sendLinkToTealium({ ...staticTealiumVariables(this.journeyName), ...sendObjToTealium });
  }

  onTenderTypeActions(tenderType: string[]): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.TENDER_TYPE}`,
      link_name: LinkName.PROCEED_TO_CHECKOUT,
      tender_type: tenderType,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onPersonalDetailsActions(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.PERSONAL_DETAILS}`,
      link_id: `${LINK_NAME}${LinkName.SHOW_BUSINESS_ADDRESS}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onOrderSummaryAction(linkName: LinkName): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.ORDER_SUMMARY}`,
      link_id: `${LINK_NAME}${linkName}`,
      journey_name: this.journeyName,
    });
  }

  onAddressDeliveryAction(): void {
    sendLinkToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.FORM_END}`,
      page_name: `${PAGE_NAME}${PageName.DELIVERY_ADDRESS}`,
      form_name: FormName.DELIVERY_ADDRESS,
      link_id: `${LINK_NAME}${LinkName.NEXT}`,
    });
  }

  onAddressDeliveryDeviceAllocationStart(): void {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.FORM_START}`,
      page_name: `${PAGE_NAME}${PageName.ALLOCATE_DEVICE}`,
      form_name: FormName.ALLOCATE_DEVICE,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onOrderConfirmAction(linkName: LinkName): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.ORDER_COMPLETE}`,
      link_id: `${LINK_NAME}${linkName}`,
    });
  }

  onConfirmOrderSubmitModalAction(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.SUBMIT_APP_CONFIRMATION}`,
      link_name: `${LINK_NAME}${LinkName.SUBMIT}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onReduceQuantityModalAction(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.REDUCE_QUANTITY_CONFIRM}`,
      link_name: `${LINK_NAME}${LinkName.REDUCE_QUANTITY_CONFIRM}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onPaymentFailureAction(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      link_page_name: `${SMME_PAGE_NAME}${PageName.PAYMENT_FAILED}`,
      link_id: `${LINK_NAME}${LinkName.PAYMENT_FAILED_RETRY}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onChooseSolutionEnd(productName: string): void {
    sendLinkToTealium({
      ...staticTealiumVariables(this.journeyName),
      event_name: EventName.UI_INTERACTION,
      product_name: productName,
      page_name: `${PAGE_NAME}${PageName.CHOOSE_SOLUTION}`,
      link_id: `${LINK_NAME}${LinkName.PROCEED_TO_CHECKOUT}`,
    });
  }

  onAddressDeliveryDeviceAllocationEnd(): void {
    sendLinkToTealium({
      event_name: `${EventName.UI_INTERACTION}, ${EventName.FORM_END}`,
      page_name: `${PAGE_NAME}${PageName.ALLOCATE_DEVICE}`,
      form_name: FormName.ALLOCATE_DEVICE,
      link_name: LinkName.DONE,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onOrderConfirmEnd(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.ORDER_COMPLETE}`,
      link_id: `${LINK_NAME}${LinkName.NEXT}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  // TAP ON PHONE
  onShowTOPModal(): void {
    sendViewToTealium({
      event_name: `${EventName.PAGE_VIEW}, ${EventName.JOURNEY_STEP}`,
      page_name: `${PAGE_NAME}${PageName.TAP_ON_PHONE_GET_STARTED}`,
      journey_step_name: 'add to cart: continue',
      ...staticTealiumVariables(JOURNEY_NAME.TAP_ON_PHONE),
    });
  }

  onCloseTOPModal(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.TAP_ON_PHONE_GET_STARTED}`,
      link_name: `${LINK_NAME}${LinkName.TAP_ON_PHONE_MODAL_CLOSE}`,
      ...staticTealiumVariables(JOURNEY_NAME.TAP_ON_PHONE),
    });
  }

  onGetItHereTOP(): void {
    sendLinkToTealium({
      event_name: EventName.UI_INTERACTION,
      page_name: `${PAGE_NAME}${PageName.TAP_ON_PHONE_GET_STARTED}`,
      link_name: `${LINK_NAME}${LinkName.TAP_ON_PHONE_GET_IT}`,
      ...staticTealiumVariables(JOURNEY_NAME.TAP_ON_PHONE),
    });
  }

  onShowReserveFundModal(transactionId: string | undefined): void {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: `${PAGE_NAME}${PageName.ORDER_SUMMARY_MODAL}`,
      transaction_id: transactionId,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onActionReserveFundModal(actionType: ActionType): void {
    sendViewToTealium({
      event_name: EventName.UI_INTERACTION,
      link_page_name: `${PAGE_NAME}${PageName.ORDER_SUMMARY_MODAL}`,
      link_name: `${LINK_NAME}${LinkName.ORDER_SUMMARY_MODAL}${actionType}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onShowConfirmModal(transactionId: string | undefined): void {
    sendViewToTealium({
      event_name: EventName.PAGE_VIEW,
      page_name: `${PAGE_NAME}${PageName.SUBMIT_APP_MODAL}`,
      transaction_id: transactionId,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onActionConfirmFundModal(actionType: ActionType): void {
    sendViewToTealium({
      event_name: EventName.UI_INTERACTION,
      link_page_name: `${PAGE_NAME}${PageName.SUBMIT_APP_MODAL}`,
      link_name: `${LINK_NAME}${LinkName.SUBMIT_APP_MODAL}${actionType}`,
      ...staticTealiumVariables(this.journeyName),
    });
  }

  onOrderSummaryEnd(): void {
    throw new Error('Method not implemented.');
  }

  onAddressDeliveryEnd(): void {
    throw new Error('Method not implemented.');
  }
}
